import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Pagination,
    Alert,
    Snackbar,
    CircularProgress,
    Typography,
    Grid,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Card,
    CardContent,
    Stack,
    FormControlLabel,
    Switch,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Autocomplete
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    BonusCalculationRule,
    BonusCalculationRulesResponse,
    BonusCalculationRuleFilters,
    BonusCalculationRuleHistory,
    CreateUpdateRuleRequest,
    Project,
    BonusCalculationRuleWithHistory,
    BonusCalculationRuleExistsResponse
} from './types';
import axiosInstance from '../../../../utils/axiosInstance';

// MAINTAINABILITY NOTE: This component handles multiple concerns (table, filters, form dialog, API calls, validation).
// For future refactoring, consider extracting:
// - useBonusRulesApi() hook for API operations (fetchRules, create, update, deactivate)
// - RuleDialog component for the form dialog
// - FiltersBar component for the filter UI
// This would improve testability and code organization.

const BRAND_NAME_OPTIONS = [
    'Highland Artisan',
    'Highland Austin',
    'Highland Dallas',
    'Highland Houston',
    'Highland San Antonio'
] as const;

const ManageBonusCalculationRules: React.FC = () => {
    const [rules, setRules] = useState<BonusCalculationRule[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [pageSize] = useState<number>(25);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number>(0);

    const [editingRule, setEditingRule] = useState<BonusCalculationRuleWithHistory | null>(null);

    const [filters, setFilters] = useState<BonusCalculationRuleFilters>({
        bonusTypeId: '1',
        bonusCalculationRuleId: '',
        ruleName: '',
        projectNumber: '',
        brandName: '',
        isActive: 'true'
    });

    const [ruleNames, setRuleNames] = useState<string[]>([]);
    const [projectNumbers, setProjectNumbers] = useState<string[]>([]);
    const [brandNames, setBrandNames] = useState<string[]>([]);

    const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
    const [selectedRule, setSelectedRule] = useState<BonusCalculationRuleWithHistory | null>(null);

    const [formOpen, setFormOpen] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [formData, setFormData] = useState<CreateUpdateRuleRequest>({
        changeNotes: ''
    });
    const [formErrors, setFormErrors] = useState<string[]>([]);

    const [percentInputValue, setPercentInputValue] = useState<string>('');
    const [fixedAmountInputValue, setFixedAmountInputValue] = useState<string>('');

    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [loadingProjects, setLoadingProjects] = useState<boolean>(false);

    const [alert, setAlert] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'info'
    });

    const showAlert = useCallback((message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        setAlert({
            open: true,
            message,
            severity
        });
    }, []);

    const fetchProjects = useCallback(async () => {
        setLoadingProjects(true);
        try {
            const res = await axiosInstance.get('/api/cba/projects');
            const data: Project[] = res.data;
            // Create a copy before sorting to avoid mutating the response
            setProjects([...data].sort((a, b) => a.subdivision.localeCompare(b.subdivision)));
        } catch (error) {
            showAlert('Failed to load projects', 'error');
            console.error('Error fetching projects:', error);
        } finally {
            setLoadingProjects(false);
        }
    }, [showAlert]);

    const fetchFilterOptions = useCallback(async () => {
        try {
            // Fetch all rules without filters to get complete list of options
            const queryParams = new URLSearchParams({
                page: '1',
                pageSize: '1000', // Large page size to get all unique values
                bonusTypeId: filters.bonusTypeId // Keep bonus type filter for relevance
            });

            const res = await axiosInstance.get(`/api/manage/bonus-calculation-rules?${queryParams.toString()}`);
            const data: BonusCalculationRulesResponse = res.data;

            const uniqueRuleNames = [...new Set(data.rules.map(r => r.ruleName).filter((name): name is string => Boolean(name)))].sort();
            const uniqueProjectNumbers = [...new Set(data.rules.map(r => r.projectNumber).filter((num): num is string => Boolean(num)))].sort();
            const uniqueBrandNames = [...new Set(data.rules.map(r => r.brandName).filter((brand): brand is string => Boolean(brand)))].sort();

            setRuleNames(uniqueRuleNames);
            setProjectNumbers(uniqueProjectNumbers);
            setBrandNames(uniqueBrandNames);
        } catch (error) {
            console.error('Error fetching filter options:', error);
            // Don't show error to user as this is a background operation
        }
    }, [filters.bonusTypeId]);

    const cleanSqlErrorMessage = useCallback((message: string): string => {
        // Remove "Error XXXXX at line XXX in schema.procedure: " prefix from SQL error messages
        const pattern = /^Error \d+ at line \d+ in [\w.]+:\s*/;
        return message.replace(pattern, '');
    }, []);

    const fetchRules = useCallback(async (customFilters?: BonusCalculationRuleFilters) => {
        setLoading(true);
        try {
            const activeFilters = customFilters || filters;

            const queryParams = new URLSearchParams({
                page: page.toString(),
                pageSize: pageSize.toString()
            });

            if (activeFilters.bonusTypeId) queryParams.append('bonusTypeId', activeFilters.bonusTypeId);
            if (activeFilters.bonusCalculationRuleId) queryParams.append('bonusCalculationRuleId', activeFilters.bonusCalculationRuleId);
            if (activeFilters.ruleName) queryParams.append('ruleName', activeFilters.ruleName);
            if (activeFilters.projectNumber) queryParams.append('projectNumber', activeFilters.projectNumber);
            if (activeFilters.brandName) queryParams.append('brandName', activeFilters.brandName);
            if (activeFilters.isActive) queryParams.append('isActive', activeFilters.isActive);

            const res = await axiosInstance.get(`/api/manage/bonus-calculation-rules?${queryParams.toString()}`);

            const data: BonusCalculationRulesResponse = res.data;
            setRules(data.rules);
            setTotalPages(data.totalPages);
            setTotalCount(data.totalCount);
        } catch (error) {
            showAlert('Failed to load bonus calculation rules', 'error');
            console.error('Error fetching rules:', error);
        } finally {
            setLoading(false);
        }
    }, [filters, page, pageSize, showAlert]);

    const checkUniqueness = useCallback(async (
        bonusTypeId: number,
        projectNumber: string,
        brandName: string,
        ruleName: string,
        currentRuleId?: number
    ): Promise<{ isUnique: boolean; conflictingRule?: BonusCalculationRule }> => {
        try {
            // Use dedicated /exists endpoint for efficient server-side check
            // This replaces the old inefficient client-side check that fetched 1000 records
            const queryParams = new URLSearchParams({
                bonusTypeId: bonusTypeId.toString()
            });

            if (ruleName && ruleName.trim()) {
                queryParams.append('ruleName', ruleName.trim());
            }

            // Add exactly one of projectNumber or brandName
            if (projectNumber && projectNumber.trim()) {
                queryParams.append('projectNumber', projectNumber.trim());
            } else if (brandName && brandName.trim()) {
                queryParams.append('brandName', brandName.trim());
            }

            // Exclude current rule when updating
            if (currentRuleId) {
                queryParams.append('excludeRuleId', currentRuleId.toString());
            }

            const res = await axiosInstance.get(
                `/api/manage/bonus-calculation-rules/exists?${queryParams.toString()}`
            );

            const data: BonusCalculationRuleExistsResponse = res.data;

            return {
                isUnique: !data.exists,
                conflictingRule: data.conflictingRule || undefined
            };
        } catch (error) {
            console.error('Error checking uniqueness:', error);
            // On error, assume unique and let server-side validation catch it
            return { isUnique: true };
        }
    }, []);

    const handleViewDetails = useCallback((rule: BonusCalculationRule) => {
        setDetailsOpen(true);
        setSelectedRule(rule as BonusCalculationRuleWithHistory);
    }, []);

    const handleCloseDetails = useCallback(() => {
        setDetailsOpen(false);
        setSelectedRule(null);
    }, []);

    const formatProjectDisplay = useCallback((project: Project) => {
        return `${project.subdivision} [${project.projectNumber}]`;
    }, []);

    const findProjectByNumber = useCallback((projectNumber: string): Project | undefined => {
        return projects.find(p => p.projectNumber === projectNumber);
    }, [projects]);

    const handleOpenCreate = useCallback(() => {
        setIsEditMode(false);
        setSelectedProject(null);
        setFormData({
            isActive: true,
            isDefault: false,
            changeNotes: ''
        });

        setPercentInputValue('');
        setFixedAmountInputValue('');

        setFormErrors([]);
        setFormOpen(true);
    }, []);

    const handleOpenEdit = useCallback((rule: BonusCalculationRule) => {
        setIsEditMode(true);
        setEditingRule(rule as BonusCalculationRuleWithHistory);

        if (rule.projectNumber) {
            const project = findProjectByNumber(rule.projectNumber);
            setSelectedProject(project || null);
        } else {
            setSelectedProject(null);
        }

        setFormData({
            id: rule.bonusCalculationRuleId,
            rowVersion: rule.rowVersion,
            isActive: rule.isActive,
            isDefault: rule.isDefault,
            ruleName: rule.ruleName,
            projectNumber: rule.projectNumber || '',
            brandName: rule.brandName || '',
            percentOfBonusPrice: rule.percentOfBonusPrice || undefined,
            fixedAmount: rule.fixedAmount || undefined,
            bonusTypeId: rule.bonusTypeId,
            changeNotes: ''
        });

        setPercentInputValue(rule.percentOfBonusPrice ? rule.percentOfBonusPrice.toString() : '');
        setFixedAmountInputValue(rule.fixedAmount ? rule.fixedAmount.toString() : '');

        setFormErrors([]);
        setFormOpen(true);
    }, [findProjectByNumber]);

    const handleCloseForm = useCallback(() => {
        setFormOpen(false);
        setFormData({ changeNotes: '' });
        setPercentInputValue('');
        setFixedAmountInputValue('');
        setEditingRule(null);
        setSelectedProject(null);
        setFormErrors([]);
    }, []);

    const handleFormChange = useCallback(<K extends keyof CreateUpdateRuleRequest>(
        field: K,
        value: CreateUpdateRuleRequest[K]
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setFormErrors(prev => prev.length > 0 ? [] : prev);
    }, []);

    const handleSave = useCallback(async () => {
        const errors: string[] = [];

        // NOTE: Validation logic differs between create and edit modes
        // Create mode: requires all fields (ruleName, bonusTypeId, project/brand, amounts)
        // Edit mode: Only allows updates to isActive, isDefault, amounts, and changeNotes
        //            Key identifying fields (ruleName, bonusTypeId, project, brand) cannot be changed
        //            Users must deactivate and create new rule to change those fields
        if (!isEditMode) {
            if (!formData.ruleName?.trim()) {
                errors.push('Rule name is required');
            }
            if (!formData.bonusTypeId) {
                errors.push('Bonus type is required');
            }

            const hasProject = formData.projectNumber?.trim();
            const hasBrand = formData.brandName?.trim();

            if (!hasProject && !hasBrand) {
                errors.push('Either Project Number or Brand Name is required');
            }
            if (hasProject && hasBrand) {
                errors.push('Cannot set both Project Number and Brand Name - choose only one');
            }

            if ((!formData.percentOfBonusPrice || formData.percentOfBonusPrice === 0) &&
                (!formData.fixedAmount || formData.fixedAmount === 0)) {
                errors.push('At least one of Percent of Bonus Price or Fixed Amount must be greater than 0');
            }
        }

        if (!formData.changeNotes?.trim()) {
            errors.push('Notes are required to document this change');
        } else if (formData.changeNotes.trim().length < 10) {
            errors.push('Notes must be at least 10 characters');
        } else if (formData.changeNotes.trim().length > 1000) {
            errors.push('Notes must not exceed 1000 characters');
        }

        if (errors.length > 0) {
            setFormErrors(errors);
            return;
        }

        setSaving(true);

        try {
            // Skip uniqueness check in edit mode since we don't allow changing project/brand
            if (!isEditMode) {
                const uniquenessCheck = await checkUniqueness(
                    formData.bonusTypeId!,
                    formData.projectNumber || '',
                    formData.brandName || '',
                    formData.ruleName || '',
                    isEditMode ? formData.id : undefined
                );

                if (!uniquenessCheck.isUnique && uniquenessCheck.conflictingRule) {
                    const conflict = uniquenessCheck.conflictingRule;
                    const projectPart = conflict.projectNumber ? `Project: ${conflict.projectNumber}` : '';
                    const brandPart = conflict.brandName ? `Brand: ${conflict.brandName}` : '';
                    const parts = [projectPart, brandPart].filter(p => p).join(', ');

                    errors.push(
                        `A rule already exists for this Bonus Type with ${parts}. ` +
                        `Conflicting rule: "${conflict.ruleName}" (ID: ${conflict.bonusCalculationRuleId})`
                    );
                    setFormErrors(errors);
                    setSaving(false);
                    return;
                }
            }

            const payload = isEditMode
                ? {
                    bonusCalculationRuleId: formData.id,
                    rowVersion: formData.rowVersion,
                    isActive: formData.isActive,
                    isDefault: formData.isDefault,
                    // NOTE: In edit mode, we don't allow changing these key fields
                    // Users must deactivate and create a new rule to change project/brand
                    // ruleName: formData.ruleName,  -- Disabled in edit mode
                    // projectNumber: formData.projectNumber,  -- Disabled in edit mode
                    // brandName: formData.brandName,  -- Disabled in edit mode
                    // bonusTypeId: formData.bonusTypeId,  -- Disabled in edit mode
                    percentOfBonusPrice: formData.percentOfBonusPrice,
                    fixedAmount: formData.fixedAmount,
                    changeNotes: formData.changeNotes
                }
                : formData;

            const res = isEditMode
                ? await axiosInstance.put('/api/manage/bonus-calculation-rules', payload)
                : await axiosInstance.post('/api/manage/bonus-calculation-rules', payload);

            const data = res.data;

            if (data.rule && data.metadata) {
                showAlert(
                    data.metadata.message || (isEditMode ? 'Rule updated successfully' : 'Rule created successfully'),
                    'success'
                );
                handleCloseForm();
                fetchRules();
            } else {
                setFormErrors(['Unexpected response from server']);
                showAlert('Unexpected response from server', 'error');
            }
        } catch (error: any) {
            console.error('Error saving rule:', error);

            let errorMessages: string[] = [];

            if (error.response?.data) {
                const errorData = error.response.data;

                // Special handling for concurrency conflicts
                if (errorData.isConcurrencyConflict || error.response.status === 409) {
                    errorMessages = [
                        'This rule was updated by someone else while you were editing. ' +
                        'Please refresh to see the latest version and re-apply your changes.'
                    ];
                    showAlert(errorMessages[0], 'warning');
                }
                else if (errorData.errors && Array.isArray(errorData.errors)) {
                    errorMessages = errorData.errors.map(cleanSqlErrorMessage);
                }
                else if (errorData.details) {
                    errorMessages = [cleanSqlErrorMessage(errorData.details)];
                }
                else if (errorData.message) {
                    errorMessages = [cleanSqlErrorMessage(errorData.message)];
                }
                else if (errorData.error) {
                    errorMessages = [cleanSqlErrorMessage(errorData.error)];
                }
                else if (typeof errorData === 'string') {
                    errorMessages = [cleanSqlErrorMessage(errorData)];
                }
                else {
                    errorMessages = ['An error occurred while saving the rule'];
                }
            } else if (error.message) {
                errorMessages = [cleanSqlErrorMessage(error.message)];
            } else {
                errorMessages = ['An unexpected error occurred'];
            }

            setFormErrors(errorMessages);
            if (!errorMessages[0].includes('updated by someone else')) {
                showAlert(errorMessages[0], 'error');
            }
        } finally {
            setSaving(false);
        }
    }, [isEditMode, formData, showAlert, handleCloseForm, fetchRules, checkUniqueness, cleanSqlErrorMessage]);

    const handleCloseAlert = useCallback(() => {
        setAlert(prev => ({ ...prev, open: false }));
    }, []);

    const handleFilterChange = useCallback((field: keyof BonusCalculationRuleFilters, value: string) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const handleSearch = useCallback(() => {
        setPage(1);
        fetchRules();
    }, [fetchRules]);

    const handleClearFilters = useCallback(() => {
        const clearedFilters = {
            bonusTypeId: '1',
            bonusCalculationRuleId: '',
            ruleName: '',
            projectNumber: '',
            brandName: '',
            isActive: 'true'
        };

        setFilters(clearedFilters);
        setPage(1);
        fetchRules(clearedFilters);
    }, [fetchRules]);

    const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    }, [handleSearch]);

    const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    }, []);

    const formatDate = useCallback((dateString: string | null) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    }, []);

    const formatCurrency = useCallback((amount: number | null) => {
        if (amount === null || amount === undefined) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    }, []);

    const formatPercentage = useCallback((value: number | null) => {
        if (value === null || value === undefined) return '-';
        return value.toFixed(5);
    }, []);

    const getBonusTypeLabel = useCallback((bonusTypeId: number) => {
        return bonusTypeId === 1 ? 'Stage' : bonusTypeId === 2 ? 'Closing' : 'Unknown';
    }, []);

    const getBonusTypeColor = useCallback((bonusTypeId: number): "default" | "primary" | "secondary" => {
        return bonusTypeId === 1 ? 'primary' : bonusTypeId === 2 ? 'secondary' : 'default';
    }, []);

    useEffect(() => {
        fetchRules();
    }, [page, fetchRules]);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    useEffect(() => {
        if (formErrors.length > 0 && formOpen) {
            const timeoutId = setTimeout(() => {
                const dialogContent = document.querySelector('[role="dialog"] .MuiDialogContent-root');
                if (dialogContent) {
                    dialogContent.scrollTop = 0;
                }
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [formErrors, formOpen]);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">
                    Bonus Calculation Rules
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreate}
                >
                    Create New Rule
                </Button>
            </Box>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Bonus Type</InputLabel>
                            <Select
                                value={filters.bonusTypeId}
                                onChange={(e) => handleFilterChange('bonusTypeId', e.target.value)}
                                label="Bonus Type"
                            >
                                <MenuItem value="1">Stage</MenuItem>
                                <MenuItem value="2">Closing</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            label="Rule ID"
                            value={filters.bonusCalculationRuleId}
                            onChange={(e) => handleFilterChange('bonusCalculationRuleId', e.target.value)}
                            onKeyPress={handleKeyPress}
                            size="small"
                            type="number"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Autocomplete
                            size="small"
                            options={['', ...ruleNames]}
                            value={filters.ruleName}
                            onChange={(event, newValue) => handleFilterChange('ruleName', newValue || '')}
                            getOptionLabel={(option) => option === '' ? 'All' : option}
                            renderInput={(params) => <TextField {...params} label="Rule Name" />}
                            isOptionEqualToValue={(option, value) => option === value}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Autocomplete
                            size="small"
                            options={[null, ...projects]}
                            value={projects.find(p => p.projectNumber === filters.projectNumber) || null}
                            onChange={(event, newValue) => handleFilterChange('projectNumber', newValue?.projectNumber || '')}
                            getOptionLabel={(option) => option === null ? 'All' : formatProjectDisplay(option)}
                            renderInput={(params) => <TextField {...params} label="Project" />}
                            isOptionEqualToValue={(option, value) => {
                                if (option === null && value === null) return true;
                                if (option === null || value === null) return false;
                                return option.projectNumber === value.projectNumber;
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Autocomplete
                            size="small"
                            options={['', ...brandNames]}
                            value={filters.brandName}
                            onChange={(event, newValue) => handleFilterChange('brandName', newValue || '')}
                            getOptionLabel={(option) => option === '' ? 'All' : option}
                            renderInput={(params) => <TextField {...params} label="Brand Name" />}
                            isOptionEqualToValue={(option, value) => option === value}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={filters.isActive}
                                onChange={(e) => handleFilterChange('isActive', e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="true">Active</MenuItem>
                                <MenuItem value="false">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                onClick={handleSearch}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : null}
                            >
                                {loading ? 'Searching...' : 'Search'}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleClearFilters}
                                disabled={loading}
                            >
                                Clear
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Showing {rules.length} of {totalCount} rules
                </Typography>
                <Tooltip title="Refresh">
                    <IconButton onClick={() => fetchRules()} size="small" disabled={loading} aria-label="Refresh rules">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Actions</TableCell>
                            <TableCell>Rule Name</TableCell>
                            <TableCell>Bonus Type</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Project Number</TableCell>
                            <TableCell>Brand</TableCell>
                            <TableCell>Percentage</TableCell>
                            <TableCell>Fixed Amount</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : rules.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    No rules found
                                </TableCell>
                            </TableRow>
                        ) : (
                            rules.map((rule) => (
                                <TableRow key={rule.bonusCalculationRuleId} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewDetails(rule)}
                                                    color="info"
                                                    aria-label={`View details for ${rule.ruleName}`}
                                                >
                                                    <InfoIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit Rule">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenEdit(rule)}
                                                    color="primary"
                                                    aria-label={`Edit ${rule.ruleName}`}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                {rule.ruleName}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                [{rule.bonusCalculationRuleId}]
                                            </Typography>
                                            {rule.isDefault && (
                                                <Chip label="Default" color="warning" size="small" />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getBonusTypeLabel(rule.bonusTypeId)}
                                            color={getBonusTypeColor(rule.bonusTypeId)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={rule.isActive ? 'Active' : 'Inactive'}
                                            color={rule.isActive ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {rule.projectNumber ? (() => {
                                            const project = findProjectByNumber(rule.projectNumber);
                                            return project ? formatProjectDisplay(project) : rule.projectNumber;
                                        })() : '-'}
                                    </TableCell>
                                    <TableCell>{rule.brandName || '-'}</TableCell>
                                    <TableCell>{formatPercentage(rule.percentOfBonusPrice)}</TableCell>
                                    <TableCell>{formatCurrency(rule.fixedAmount)}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {totalPages > 1 && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                    />
                </Box>
            )}

            <Dialog
                open={detailsOpen}
                onClose={handleCloseDetails}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Rule Details</Typography>
                        <IconButton onClick={handleCloseDetails} size="small" aria-label="Close details">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedRule && (
                        <Stack spacing={3}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="h6" gutterBottom color="primary">
                                        Rule Information
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Rule ID</Typography>
                                            <Typography variant="body1">{selectedRule.bonusCalculationRuleId}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Rule Name</Typography>
                                            <Typography variant="body1">{selectedRule.ruleName}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Bonus Type</Typography>
                                            <Box sx={{ mt: 0.5 }}>
                                                <Chip
                                                    label={getBonusTypeLabel(selectedRule.bonusTypeId)}
                                                    color={getBonusTypeColor(selectedRule.bonusTypeId)}
                                                    size="small"
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Status</Typography>
                                            <Box sx={{ mt: 0.5 }}>
                                                <Chip
                                                    label={selectedRule.isActive ? 'Active' : 'Inactive'}
                                                    color={selectedRule.isActive ? 'success' : 'default'}
                                                    size="small"
                                                />
                                                {selectedRule.isDefault && (
                                                    <Chip label="Default" color="warning" size="small" sx={{ ml: 1 }} />
                                                )}
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Project</Typography>
                                            {selectedRule.projectNumber ? (
                                                <>
                                                    <Typography variant="body1">
                                                        {(() => {
                                                            const project = findProjectByNumber(selectedRule.projectNumber);
                                                            return project ? formatProjectDisplay(project) : selectedRule.projectNumber;
                                                        })()}
                                                    </Typography>
                                                    {(() => {
                                                        const project = findProjectByNumber(selectedRule.projectNumber);
                                                        return project ? (
                                                            <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                                <Typography variant="caption" color="text.secondary" display="block">
                                                                    <strong>Company:</strong> {project.companyName || 'N/A'}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" display="block">
                                                                    <strong>Area:</strong> {project.areaName}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" display="block">
                                                                    <strong>Hierarchy:</strong> {project.hierLevel1} → {project.hierLevel2} → {project.hierLevel3} → {project.hierLevel4}
                                                                </Typography>
                                                            </Box>
                                                        ) : null;
                                                    })()}
                                                </>
                                            ) : (
                                                <Typography variant="body1">-</Typography>
                                            )}
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Brand Name</Typography>
                                            <Typography variant="body1">{selectedRule.brandName || '-'}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Percent of Bonus Price</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                                                {formatPercentage(selectedRule.percentOfBonusPrice)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Fixed Amount</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                                                {formatCurrency(selectedRule.fixedAmount)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="h6" gutterBottom color="primary">
                                        Audit Information
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Created By</Typography>
                                            <Typography variant="body1">{selectedRule.createdBy}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(selectedRule.createdOn)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Last Updated By</Typography>
                                            <Typography variant="body1">{selectedRule.updatedBy || '-'}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(selectedRule.updatedOn)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary">Row Version</Typography>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                {selectedRule.rowVersion}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            <Accordion defaultExpanded={false}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="history-content"
                                    id="history-header"
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <HistoryIcon sx={{ mr: 1 }} color="primary" />
                                        <Typography variant="h6" color="primary">
                                            Change History
                                        </Typography>
                                        {selectedRule.history && selectedRule.history.length > 0 && (
                                            <Chip
                                                label={`${selectedRule.history.length} changes`}
                                                size="small"
                                                sx={{ ml: 2 }}
                                                color="info"
                                            />
                                        )}
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {!selectedRule.history || selectedRule.history.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            No history records found
                                        </Typography>
                                    ) : (
                                        <Stack spacing={2}>
                                            {selectedRule.history.map((history: BonusCalculationRuleHistory, index: number) => (
                                                <Card key={history.id} variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                                                    <CardContent>
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={12}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                    <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                                                                        Change #{(selectedRule.history?.length || 0) - index}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {formatDate(history.historyCreatedOn)}
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="caption" color="text.secondary">Changed By</Typography>
                                                                <Typography variant="body2">
                                                                    {history.changedBy || history.updatedBy || history.createdBy}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="caption" color="text.secondary">Operation</Typography>
                                                                <Typography variant="body2">
                                                                    {history.operationType || 'N/A'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="caption" color="text.secondary">Status</Typography>
                                                                <Box sx={{ mt: 0.5 }}>
                                                                    <Chip
                                                                        label={history.isActive ? 'Active' : 'Inactive'}
                                                                        color={history.isActive ? 'success' : 'default'}
                                                                        size="small"
                                                                    />
                                                                    {history.isDefault && (
                                                                        <Chip label="Default" color="warning" size="small" sx={{ ml: 1 }} />
                                                                    )}
                                                                </Box>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="caption" color="text.secondary">Rule Name</Typography>
                                                                <Typography variant="body2">{history.ruleName}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="caption" color="text.secondary">Project Number</Typography>
                                                                <Typography variant="body2">{history.projectNumber || '-'}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="caption" color="text.secondary">Brand Name</Typography>
                                                                <Typography variant="body2">{history.brandName || '-'}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="caption" color="text.secondary">Percentage</Typography>
                                                                <Typography variant="body2">{formatPercentage(history.percentOfBonusPrice)}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Typography variant="caption" color="text.secondary">Fixed Amount</Typography>
                                                                <Typography variant="body2">{formatCurrency(history.fixedAmount)}</Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography variant="caption" color="text.secondary">Notes</Typography>
                                                                <Paper sx={{ p: 1, mt: 0.5, bgcolor: 'background.paper' }}>
                                                                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                                        {history.notes || 'No notes available'}
                                                                    </Typography>
                                                                </Paper>
                                                            </Grid>
                                                        </Grid>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </Stack>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDetails}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={formOpen}
                onClose={handleCloseForm}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {isEditMode ? 'Edit Rule' : 'Create New Rule'}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        {formErrors.length > 0 && (
                            <Alert
                                severity="error"
                                sx={{
                                    mb: 2,
                                    '& .MuiAlert-message': {
                                        width: '100%'
                                    }
                                }}
                                onClose={() => setFormErrors([])}
                            >
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Please fix the following errors:
                                </Typography>
                                <Box component="ul" sx={{ margin: 0, paddingLeft: 2, mt: 1 }}>
                                    {formErrors.map((error, index) => (
                                        <li key={index}>
                                            <Typography variant="body2">{error}</Typography>
                                        </li>
                                    ))}
                                </Box>
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            label="Rule Name *"
                            value={formData.ruleName || ''}
                            onChange={(e) => handleFormChange('ruleName', e.target.value)}
                            disabled={isEditMode}
                        />

                        <Autocomplete
                            fullWidth
                            disabled={isEditMode}
                            options={[
                                { id: 1, label: 'Stage' },
                                { id: 2, label: 'Closing' }
                            ]}
                            value={formData.bonusTypeId ? { id: formData.bonusTypeId, label: formData.bonusTypeId === 1 ? 'Stage' : 'Closing' } : null}
                            onChange={(event, newValue) => handleFormChange('bonusTypeId', newValue?.id || undefined)}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => <TextField {...params} label="Bonus Type *" />}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                        />

                        <Divider />

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    fullWidth
                                    disabled={isEditMode}
                                    options={[null, ...projects]}
                                    value={selectedProject}
                                    onChange={(event, newValue) => {
                                        const projectNumber = newValue?.projectNumber || '';
                                        setSelectedProject(newValue);
                                        handleFormChange('projectNumber', projectNumber);
                                        if (newValue) {
                                            handleFormChange('projectId', newValue.projectId);
                                        }
                                    }}
                                    getOptionLabel={(option) => option === null ? 'None' : formatProjectDisplay(option)}
                                    renderInput={(params) => <TextField {...params} label="Project" />}
                                    isOptionEqualToValue={(option, value) => {
                                        if (option === null && value === null) return true;
                                        if (option === null || value === null) return false;
                                        return option.projectNumber === value.projectNumber;
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    fullWidth
                                    disabled={isEditMode}
                                    options={['', ...BRAND_NAME_OPTIONS]}
                                    value={formData.brandName || ''}
                                    onChange={(event, newValue) => handleFormChange('brandName', newValue || '')}
                                    getOptionLabel={(option) => option === '' ? 'None' : option}
                                    renderInput={(params) => <TextField {...params} label="Brand Name" />}
                                    isOptionEqualToValue={(option, value) => option === value}
                                />
                            </Grid>
                        </Grid>

                        {selectedProject && (
                            <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                                <CardContent>
                                    <Typography variant="subtitle2" gutterBottom color="primary">
                                        Project Details
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Company</Typography>
                                            <Typography variant="body2">{selectedProject.companyName || 'N/A'}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Area</Typography>
                                            <Typography variant="body2">{selectedProject.areaName}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Level 1</Typography>
                                            <Typography variant="body2">{selectedProject.hierLevel1}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Level 2</Typography>
                                            <Typography variant="body2">{selectedProject.hierLevel2}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Level 3</Typography>
                                            <Typography variant="body2">{selectedProject.hierLevel3}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" color="text.secondary">Level 4</Typography>
                                            <Typography variant="body2">{selectedProject.hierLevel4}</Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}

                        {isEditMode ? (
                            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                                Project and Brand cannot be changed when editing. To apply this rule to a different project or brand,
                                deactivate this rule and create a new one.
                            </Alert>
                        ) : (
                            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                                Either Project Number or Brand Name must be provided (not both)
                            </Alert>
                        )}

                        <Divider />

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Percent of Bonus Price"
                                    value={percentInputValue}
                                    disabled={!!fixedAmountInputValue && fixedAmountInputValue !== ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === '' || /^\d*\.?\d{0,5}$/.test(value)) {
                                            setPercentInputValue(value);
                                            // Clear fixed amount when percent is entered
                                            if (value !== '' && value !== '.') {
                                                setFixedAmountInputValue('');
                                                handleFormChange('fixedAmount', undefined);
                                            }
                                            // Prevent NaN from entering formData - only update if valid number
                                            if (value === '' || value === '.') {
                                                handleFormChange('percentOfBonusPrice', undefined);
                                            } else {
                                                const numValue = parseFloat(value);
                                                if (Number.isFinite(numValue)) {
                                                    handleFormChange('percentOfBonusPrice', numValue);
                                                }
                                            }
                                        }
                                    }}
                                    onBlur={() => {
                                        if (percentInputValue) {
                                            const numValue = parseFloat(percentInputValue);
                                            if (!isNaN(numValue)) {
                                                if (numValue > 1) {
                                                    setPercentInputValue('1');
                                                    handleFormChange('percentOfBonusPrice', 1);
                                                } else if (numValue < 0) {
                                                    setPercentInputValue('0');
                                                    handleFormChange('percentOfBonusPrice', 0);
                                                }
                                            }
                                        }
                                    }}
                                    helperText="Decimal value from 0 to 1 (e.g., 0.05 for 5%, 0.00125 for 0.125%)"
                                    placeholder="0.00500"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Fixed Amount"
                                    value={fixedAmountInputValue}
                                    disabled={!!percentInputValue && percentInputValue !== ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                                            setFixedAmountInputValue(value);
                                            // Clear percent when fixed amount is entered
                                            if (value !== '' && value !== '.') {
                                                setPercentInputValue('');
                                                handleFormChange('percentOfBonusPrice', undefined);
                                            }
                                            // Prevent NaN from entering formData - only update if valid number
                                            if (value === '' || value === '.') {
                                                handleFormChange('fixedAmount', undefined);
                                            } else {
                                                const numValue = parseFloat(value);
                                                if (Number.isFinite(numValue)) {
                                                    handleFormChange('fixedAmount', numValue);
                                                }
                                            }
                                        }
                                    }}
                                    onBlur={() => {
                                        if (fixedAmountInputValue) {
                                            const numValue = parseFloat(fixedAmountInputValue);
                                            if (!isNaN(numValue)) {
                                                if (numValue > 100000) {
                                                    setFixedAmountInputValue('100000.00');
                                                    handleFormChange('fixedAmount', 100000);
                                                } else if (numValue < 0) {
                                                    setFixedAmountInputValue('0');
                                                    handleFormChange('fixedAmount', 0);
                                                }
                                            }
                                        }
                                    }}
                                    helperText="Dollar amount (up to 2 decimal places, max $100,000)"
                                    placeholder="5000.00"
                                    InputProps={{
                                        startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                            At least one of Percent of Bonus Price or Fixed Amount must be greater than 0
                        </Alert>

                        <Divider />

                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive || false}
                                        onChange={(e) => handleFormChange('isActive', e.target.checked)}
                                    />
                                }
                                label="Active"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isDefault || false}
                                        onChange={(e) => handleFormChange('isDefault', e.target.checked)}
                                    />
                                }
                                label="Set as Default for this Bonus Type"
                            />
                        </Box>

                        {formData.isDefault && (
                            <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>
                                Setting this as default will automatically unset any other default rules for this Bonus Type
                            </Alert>
                        )}

                        {isEditMode && editingRule && editingRule.history && editingRule.history.length > 0 && (
                            <>
                                <Divider />

                                <Accordion defaultExpanded={false}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls="edit-history-content"
                                        id="edit-history-header"
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <HistoryIcon sx={{ mr: 1 }} color="primary" />
                                            <Typography variant="h6" color="primary">
                                                Change History
                                            </Typography>
                                            <Chip
                                                label={`${editingRule.history.length} changes`}
                                                size="small"
                                                sx={{ ml: 2 }}
                                                color="info"
                                            />
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                                            <Stack spacing={2}>
                                                {editingRule.history.slice(0, 5).map((history: BonusCalculationRuleHistory, index: number) => (
                                                    <Card key={history.id} variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                            <Grid container spacing={1}>
                                                                <Grid item xs={12}>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                        <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                                                                            Change #{(editingRule.history?.length ?? 0) - index}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {formatDate(history.historyCreatedOn)}
                                                                        </Typography>
                                                                    </Box>
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="caption" color="text.secondary">Changed By</Typography>
                                                                    <Typography variant="body2" fontSize="0.8rem">
                                                                        {history.changedBy || history.updatedBy || history.createdBy}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="caption" color="text.secondary">Operation</Typography>
                                                                    <Typography variant="body2" fontSize="0.8rem">
                                                                        {history.operationType || 'N/A'}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="caption" color="text.secondary">Status</Typography>
                                                                    <Box sx={{ mt: 0.5 }}>
                                                                        <Chip
                                                                            label={history.isActive ? 'Active' : 'Inactive'}
                                                                            color={history.isActive ? 'success' : 'default'}
                                                                            size="small"
                                                                        />
                                                                        {history.isDefault && (
                                                                            <Chip label="Default" color="warning" size="small" sx={{ ml: 0.5 }} />
                                                                        )}
                                                                    </Box>
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="caption" color="text.secondary">Rule Name</Typography>
                                                                    <Typography variant="body2" fontSize="0.8rem">{history.ruleName}</Typography>
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="caption" color="text.secondary">Project Number</Typography>
                                                                    <Typography variant="body2" fontSize="0.8rem">{history.projectNumber || '-'}</Typography>
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="caption" color="text.secondary">Brand Name</Typography>
                                                                    <Typography variant="body2" fontSize="0.8rem">{history.brandName || '-'}</Typography>
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="caption" color="text.secondary">Percentage</Typography>
                                                                    <Typography variant="body2" fontSize="0.8rem">{formatPercentage(history.percentOfBonusPrice)}</Typography>
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <Typography variant="caption" color="text.secondary">Fixed Amount</Typography>
                                                                    <Typography variant="body2" fontSize="0.8rem">{formatCurrency(history.fixedAmount)}</Typography>
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                                                                    <Paper sx={{ p: 0.5, mt: 0.5, bgcolor: 'background.paper' }}>
                                                                        <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                                                                            {history.notes || 'No notes available'}
                                                                        </Typography>
                                                                    </Paper>
                                                                </Grid>
                                                            </Grid>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </Stack>
                                        </Box>

                                        {editingRule.history.length > 5 && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                                                Showing 5 most recent changes out of {editingRule.history.length} total
                                            </Typography>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </>
                        )}

                        <Divider />

                        <TextField
                            fullWidth
                            label="Notes *"
                            multiline
                            rows={4}
                            value={formData.changeNotes}
                            onChange={(e) => handleFormChange('changeNotes', e.target.value)}
                            helperText={
                                formData.changeNotes && formData.changeNotes.length > 1000
                                    ? `⚠️ ${formData.changeNotes.length}/1000 characters - Please keep notes concise`
                                    : `${formData.changeNotes?.length || 0}/1000 characters. Minimum 10 characters. Explain the reason for this change.`
                            }
                            required
                            inputProps={{
                                maxLength: 1000
                            }}
                            error={(formData.changeNotes?.length ?? 0) > 1000} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseForm} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={alert.open}
                autoHideDuration={6000}
                onClose={handleCloseAlert}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
                    {alert.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ManageBonusCalculationRules;